

/** These setting variables will be turned into CSS variables based on the object tree, i.e.
 * --main-text-font: 'Ubuntu',
 * --main-text-size: '1rem',
 * --main-text-color: '#333',
 * --main-background-color: '#232',
 * --main-background-image: 'url(12323)',
 * --content-text-font: 'Ubuntu',
 * --content-text-size: '1rem',
 * --content-text-color: '#333',
 * ...
 *
 * SASS like functions are supported which will be resolved in javascript before rendering the CSS variable.
 *
 */

export const THEME_OFF_WHITE = '#f7f7f7';
export const THEME_OFF_BLACK = '#676a6c';
// The luminance above which a fill takes dark text rather than light. Higher than inverse()'s
// default, which is set for a page background: a saturated mid-tone reads as light on the YIQ
// scale (success 146, info 165, warning 182) and would take dark text beside a near-white at 245.
const THEME_FILL_CONTRAST_THRESHOLD = 175;
export const THEME_SYSTEM_FONT = 'System';
export const THEME_SYSTEM_FONT_STACK = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const THEME_DEFAULTS = {

    main: {
        font: {
            family: 'Inter',
            size: '1rem',
            weight: '400',
            color: `var(--main-background-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}")`,
        },
        background: {
            color: THEME_OFF_WHITE,
        },
        border: {
            color: 'var(--main-background-color).darken(0.1)',
        },
        gutter: {
            width: '0',
        },
        shadow: 'none',
        icon: {
            pack: 'fontawesome:900',
        },
    },

    content: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
        },
        background: {
            color: 'var(--main-background-color).lighten(0.1)',
        },
        border: {
            width: '1px',
            color: 'var(--content-background-color).darken(0.1)',
        },
        shadow: 'none',
    },

    sidebar: {
        font: {
            family: 'var(--content-font-family)',
            size: '1rem',
            color: 'var(--content-font-color)',
        },
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--sidebar-background-color).darken(0.05)',
        },
        shadow: 'none',
    },

    loader: {
        style: 'spinner',
        font: {
            color: 'var(--main-font-color)',
        },
        overlay: {
            background: {
                color: 'var(--main-background-color).fade(0.6)',
            },
        },
    },

    // soft and edge are the two translucent weights a role is painted at — soft is a tint behind
    // text, edge is an outline's border. They are alpha rather than a blend with a surface colour,
    // so one value composites over a card, the page or a hovered table row alike. Each is derived
    // from its own role and can be overridden on its own by a theme.
    //
    // fade() is relative — it scales the alpha already there rather than setting it — so
    // fade(0.88) leaves 12% of an opaque colour. See rad-ui/docs/foundations/colour.mdx § 6.
    //
    // Every role carries both. Whether a given weight is usable depends on the colour a vendor
    // sets, not on the role, so none of them is excluded here on the strength of its default.
    color: {
        primary: { color: '#f33636', contrast: `var(--color-primary-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, soft: 'var(--color-primary-color).fade(0.88)', edge: 'var(--color-primary-color).fade(0.7)', },
        secondary: { color: 'var(--content-background-color).darken(0.05)', contrast: `var(--color-secondary-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, soft: 'var(--color-secondary-color).fade(0.88)', edge: 'var(--color-secondary-color).fade(0.7)', },
        success: { color: '#5cb85c', contrast: `var(--color-success-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, soft: 'var(--color-success-color).fade(0.88)', edge: 'var(--color-success-color).fade(0.7)', },
        info: { color: '#5bc0de', contrast: `var(--color-info-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, soft: 'var(--color-info-color).fade(0.88)', edge: 'var(--color-info-color).fade(0.7)', },
        notify: { color: '#f9f6e8', contrast: `var(--color-notify-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, soft: 'var(--color-notify-color).fade(0.88)', edge: 'var(--color-notify-color).fade(0.7)', },
        warning: { color: '#f0ad4e', contrast: `var(--color-warning-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, soft: 'var(--color-warning-color).fade(0.88)', edge: 'var(--color-warning-color).fade(0.7)', },
        danger: { color: '#d9534f', contrast: `var(--color-danger-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, soft: 'var(--color-danger-color).fade(0.88)', edge: 'var(--color-danger-color).fade(0.7)', },
        light: { color: '#F5F5F5', contrast: `var(--color-light-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, },
        dark: { color: '#55595c', contrast: `var(--color-dark-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}", ${THEME_FILL_CONTRAST_THRESHOLD})`, },
    },

    header: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: '400',
            transform: 'unset',
        },
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--header-background-color).darken(0.05)',
        },
    },

    subheader: {
        font: {
            family: 'var(--content-font-family)',
            color: 'var(--content-font-color)',
            size: '1rem',
            weight: '400',
            transform: 'unset',
        },
        background: {
            color: 'var(--content-background-color).darken(0.025)',
        },
        border: {
            color: 'var(--subheader-background-color).darken(0.05)',
        },
    },

    footer: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
        },
        background: {
            color: 'var(--content-background-color).darken(0.025)',
        },
        border: {
            width: '1px',
            color: 'var(--footer-background-color).darken(0.1)',
        },
    },

    navigation: {
        size: 'auto',
        position: 'left',
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: '400',
            transform: 'unset',
        },
        icon: {
            color: 'inherit',
        },
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--navigation-background-color).darken(0.1)',
        },
        separator: {
            width: '0px',
            color: 'var(--main-border-color)',
        },
        active: {
            style: 'left-border',
            font: {
                color: 'var(--color-primary-contrast)',
            },
            background: {
                color: 'var(--color-primary-color)',
            },
        },
        subnav: {
            font: {
                color: 'var(--main-font-color)',
            },
            background: {
                color: 'var(--navigation-background-color).darken(0.05)',
            },
        },
        client: {
            font: {
                family: 'var(--main-font-family)',
                size: '1rem',
                color: 'var(--footer-font-color)',
            },
            background: {
                color: 'var(--footer-background-color)',
            },
            border: {
                width: '1px',
                color: 'var(--footer-border-color)',
            },
        },
        logo: {
            background: {
                color: '#ffffff',
            },
            border: {
                width: '1px',
                color: 'var(--navigation-logo-background-color).darken(0.1)',
            },
        },
    },

    topnavigation: {
        style: 'hidden',
        font: {
            family: 'var(--header-font-family)',
            size: '1rem',
            color: 'var(--header-font-color)',
            weight: '400',
        },
        background: {
            color: 'var(--header-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--topnavigation-background-color).darken(0.1)',
        },
        subnav: {
            font: {
                color: 'var(--header-font-color)',
            },
            background: {
                color: 'var(--topnavigation-background-color).darken(0.05)',
            },
        },
        active: {
            font: {
                color: 'var(--color-primary)',
            },
            background: {
                color: 'transparent',
            },
            tab: {
                font: {
                    color: 'var(--topnavigation-font-color)',
                },
                background: {
                    color: 'var(--topnavigation-background-color).darken(0.05)',
                },
            },
            indicator: {
                style: 'var(--tab-active-indicator-style)',
                color: 'var(--tab-active-indicator-color)',
            },
        },
        logo: {
            style: 'right',
        },

    },

    accordion: {
        font: {
            family: 'var(--main-font-family)',
            color: 'var(--main-font-color)',
            size: '0.9rem',
            weight: '500',
            transform: 'uppercase',
        },
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--accordion-background-color).darken(0.05)',
        },
        active: {
            font: {
                color: 'var(--subheader-font-color)',
            },
            background: {
                color: 'var(--subheader-background-color)',
            },
            border: {
                width: '1px',
                color: 'var(--accordion-active-background-color).darken(0.1)',
            },
        },
    },

    table: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: 'var(--main-font-weight)',
        },
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--table-background-color).darken(0.05)',
        },
        header: {
            font: {
                family: 'var(--subheader-font-family)',
                size: '1rem',
                color: 'var(--subheader-font-color)',
                weight: '500',
                transform: 'unset',
            },
            background: {
                color: 'var(--subheader-background-color)',
            },
            border: {
                width: '1px',
                color: 'var(--table-header-background-color).darken(0.1)',
            },
        },
        active: {
            font: {
                color: 'var(--color-primary-contrast)',
            },
            background: {
                color: 'var(--color-primary-color)',
            },
            border: {
                width: '1px',
                color: 'var(--table-active-background-color).darken(0.1)',
            },
        },

    },

    tree: {
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--tree-background-color).darken(0.1)',
        },
        active: {
            font: {
                color: 'var(--color-primary-contrast)',
            },
            background: {
                color: 'var(--color-primary-color)',
            },
        },

    },

    list: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: 'var(--main-font-weight)',
        },
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--list-background-color).darken(0.05)',
        },
        icon: {
            color: 'var(--list-background-color).darken(0.1)',
        },
        active: {
            font: {
                color: 'var(--main-font-color)',
            },
            icon: {
                color: 'var(--color-primary-color)',
            },
            background: {
                color: 'var(--list-background-color).darken(0.025)',
            },
        },
    },

    feed: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: 'var(--main-font-weight)',
        },
        background: {
            color: 'var(--content-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--feed-background-color).darken(0.05)',
        },
        header: {
            font: {
                color: 'var(--content-font-color)',
            },
            background: {
                color: 'var(--feed-background-color)',
            },
            border: {
                color: 'var(--feed-background-color).darken(0.05)',
            },
        },
        active: {
            font: {
                color: 'var(--main-font-color)',
            },
            background: {
                color: 'var(--feed-background-color).darken(0.025)',
            },
        },

    },

    tab: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: 'var(--main-font-weight)',
            transform: 'unset',
        },
        background: {
            color: 'var(--header-background-color)',
        },
        border: {
            width: '1px',
            color: 'var(--tab-background-color).darken(0.05)',
        },
        active: {
            font: {
                color: 'var(--main-font-color)',
            },
            background: {
                color: 'var(--tab-background-color).darken(0.05)',
            },
            indicator: {
                style: 'none',
                color: 'var(--color-primary-color)',
            },
        },

    },

    input: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: 'var(--main-font-weight)',
        },
        background: {
            color: 'var(--content-background-color).lighten(0.1)',
        },
        border: {
            width: '1px',
            color: 'var(--content-background-color).darken(0.1)',
        },
    },

    button: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: 'var(--main-font-weight)',
            transform: 'unset',
        },
        border: {
            radius: '2px',
            shadow: 'none',
        },
        hover: {
            border: {
                shadow: 'none',
            },
        },
        active: {
            border: {
                shadow: 'none',
            },
        },
    },

    snackbar: {
        font: {
            family: 'var(--main-font-family)',
            size: '1rem',
            color: 'var(--main-font-color)',
            weight: 'var(--main-font-weight)',
        },
        background: {
            color: 'var(--content-background-color).lighten(0.2)',
        },
        border: {
            width: '1px',
            color: 'var(--snackbar-background-color).darken(0.1)',
        },
    },

    map: {
        control: {
            font: {
                family: 'var(--main-font-family)',
                size: '1rem',
                color: '#676a6c',
                weight: 'var(--main-font-weight)',
            },
            background: {
                color: '#fff',
            },
            secondary: {
                background: {
                    color: 'var(--map-control-background-color).darken(0.05)',
                },
            },
            border: {
                width: '1px',
                color: 'var(--map-control-background-color).darken(0.1)',
            },
        },
    },

    graph: {
        palette: 'default',
        label: {
            font: {
                family: 'var(--main-font-family)',
                size: '0.8rem',
                color: 'var(--main-font-color).lighten(0.5)',
                weight: 'var(--main-font-weight)',
            },
        },
    },

    modal: {

        font: {
            family: 'var(--content-font-family)',
            size: '1rem',
            color: 'var(--content-font-color)',
        },

        background: {
            color: 'var(--content-background-color)',
        },

        border: {
            width: '1px',
            color: 'var(--content-border-color)',
            radius: '0px',
        },

        shadow: '1px 2px 5px 0 rgba(0, 0, 0, 0.2)',

        header: {
            font: {
                family: 'var(--header-font-family)',
                size: '1rem',
                color: 'var(--header-font-color)',
                weight: 'var(--header-font-weight)',
                transform: 'var(--header-font-transform)',
            },
            background: {
                color: 'var(--header-background-color)',
            },
            border: {
                width: '1px',
                color: 'var(--header-border-color)',
            },
        },

        footer: {
            font: {
                family: 'var(--footer-font-family)',
                size: '1rem',
                color: 'var(--footer-font-color)',
            },
            background: {
                color: 'var(--footer-background-color)',
            },
            border: {
                width: '1px',
                color: 'var(--footer-border-color)',
            },
        },

    },

    // RAD UI. Not yet exposed in the theme editor. See src/app/rad-ui/docs/foundations/colour.mdx.
    rad: {
        surface: {
            page: { background: { color: 'var(--main-background-color)' } },
            raised: { background: { color: 'var(--content-background-color)' } },
            sunken: { background: { color: 'var(--main-background-color).darken(0.03)' } },
        },
        // Text — a plain rem per step, editable in the theme editor. Each step is an element with
        // its own font container, so a future Text panel binds to it the way component sections do.
        // Only the spacing scale stays in rad-ui's SCSS, because a theme value containing calc() is
        // evaluated as JavaScript by the engine and resolves to black. See
        // foundations/typography.mdx and standards/theming.mdx.
        text: {
            // soft sits beside the font colour it is drawn from, the way the brand and status roles
            // carry theirs in the `color` group. It is what a neutral chip is tinted with, so an
            // unremarkable badge stays in the vendor's own text hue.
            //
            // There is no matching edge: a neutral chip's border is the hairline every other
            // surface draws, --rad-border-normal-color, rather than a grey of its own.
            default: { font: { color: 'var(--main-font-color)', weight: 'var(--main-font-weight)' }, soft: 'var(--rad-text-default-font-color).fade(0.88)' },
            muted: { font: { color: 'var(--main-font-color).fade(0.35)' }, soft: 'var(--rad-text-muted-font-color).fade(0.88)' },
            strong: { font: { weight: '500' } },
            // md is the vendor's own base size, so rad body text is the size the rest of the app
            // runs at. The other steps are plain rems landing on the design scale's pixel values at
            // that base, so only md moves when a vendor changes it.
            xxxl: { font: { size: '2.5rem', 'line-height': '1.05' } },
            xxl: { font: { size: '2rem', 'line-height': '1.05' } },
            xl: { font: { size: '1.54rem', 'line-height': '1.1' } },
            lg: { font: { size: '1.25rem', 'line-height': '1.2' } },
            md: { font: { size: 'var(--main-font-size)', 'line-height': '1.3' } },
            sm: { font: { size: '0.92rem', 'line-height': '1.3' } },
            xs: { font: { size: '0.77rem', 'line-height': '1.3' } },
        },
        measure: {
            body: '68ch',
            narrow: '40ch',
        },
        border: {
            normal: { color: 'var(--content-border-color)' },
            subtle: { color: 'var(--content-border-color).lighten(0.05)' },
            strong: { color: 'var(--main-border-color)' },
        },
        // Chart series — the one part of RAD that is not calculated from the vendor's own colours.
        // A set derived from the brand collides with the status roles: the accent group this
        // replaces rotated a red brand into amber and yellow-green, which read as warning and
        // success. A series colour has to differ from the ones beside it rather than mean anything,
        // so the eight are fixed and a vendor overrides them the way they override any other value.
        //
        // The order is the colour-blind safety mechanism, not a preference: a chart takes slot 1,
        // then 2, then 3, and never cycles. Measured on this order, the worst neighbouring pair is
        // 8.4 under protanopia and 19.3 under normal vision (OKLab ΔE ×100, floors 8 and 15).
        //
        // One set serves a light and a dark theme, because every step sits in the lightness band
        // the two modes share. Slot 6 falls under 3:1 against a dark card, which the legend and the
        // axis labels answer — a series is never identified by its colour alone.
        // See foundations/colour.mdx § 7.
        chart: {
            series: {
                1: { color: '#3987e5' },
                2: { color: '#d95926' },
                3: { color: '#199e70' },
                4: { color: '#c98500' },
                5: { color: '#d55181' },
                6: { color: '#008300' },
                7: { color: '#9085e9' },
                8: { color: '#e66767' },
            },
        },
    },

};

export const THEME_GRAPH_PALETTES = {
    default: ['#69aaf8', '#f33636', '#F5B400', '#5F39BF', '#F47C2A', '#B199F4', '#86E57E', '#857986', '#6b0392', '#f05b4f', '#dda458', '#eacf7d', '#86797d', '#b2c326', '#6188e2', '#a748ca', '#69aaf8', '#F5B400', '#5F39BF', '#F47C2A', '#B199F4', '#86E57E', '#857986', '#6b0392', '#f05b4f', '#dda458', '#eacf7d'],
    nightrider: ['rgba(91, 189, 218, 0.8)', '#b32a2a', '#fdcdcd', '#cbccce', '#b0e1d8', '#eee9b4', '#d3c6d6', '#aaaaaa', '#88b7b8', '#e0bba9', '#ffda90', '#a8cca0', '#969abd', '#ba9db6'],
};
